import { Link } from 'react-router';
import { archivedPdfUrl } from '@/api/client';
import type { ReactNode } from 'react';
import { Tag } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { money, num, dateTime } from '@/lib/format';
import { signedMoney } from './api';
import { EPS, LABELS, lastAction, statementStatus } from './status';
import type { StatementListItem } from './types';
import type { Col } from './ui';
import s from './StatementList.module.css';

const scopeOf = (x: StatementListItem) => (x.partnerType === 'SUPPLIER' ? 'supplier' : 'customer');

/** 正式 PDF 存在 V1 共用归档中，V1/V2 均可读取。 */
export function PdfLink({ url, compact }: { url?: string | null; compact?: boolean }) {
  const safeUrl = archivedPdfUrl(url);
  if (!safeUrl) return null;
  return (
    <a href={safeUrl} target="_blank" rel="noreferrer" className={compact ? s.pdfC : s.pdf} onClick={(e) => e.stopPropagation()}>
      <Icon name="file" size={14} />PDF
    </a>
  );
}

function Owe({ v }: { v?: number }) {
  if (v === undefined) return <span className="em">—</span>;
  const n = num(v);
  return <span className={`tnum ${n > EPS ? s.owe : n < -EPS ? s.over : 'em'}`}>{signedMoney(n)}</span>;
}

/**
 * 对账单卡（高保真稿 T7）：单号 + 状态（全卡唯一实底标签）→ 单位 · 类型 · 月份 → 三格金额 → 最近动作。
 * 已作废的卡片整体变灰，金额写「—」。本地允许下一步状态操作。
 */
export function StatementCard({ x, to, selected, actions }: { x: StatementListItem; to?: string; selected?: boolean; actions?: ReactNode }) {
  const L = LABELS[scopeOf(x)];
  const st = statementStatus[x.status];
  const act = lastAction(x);
  const cancelled = x.status === 'CANCELLED';
  const amounts = x.billAmount !== undefined;
  const content = <>
    <div className={s.h}>
      <b className="tnum">{x.statementNo}</b>
      <Tag tone={st.tone} sm>{st.label}</Tag>
    </div>
    <div className={s.sub}>{x.partner?.name || '—'} · {L.partner} · <span className="tnum">{x.settlementMonth}</span></div>
    {amounts && (
      <div className={s.g}>
        <div><i>业务金额</i><b className="tnum">{cancelled ? '—' : money(x.billAmount)}</b></div>
        <div><i>{L.cashShort}</i><b className="tnum">{cancelled ? '—' : money(x.cashAmount)}</b></div>
        <div><i>{num(x.cashBalance) < -EPS ? L.over : L.open}</i><b>{cancelled ? '—' : <Owe v={x.cashBalance} />}</b></div>
      </div>
    )}
  </>;
  return (
    <article className={[s.card, cancelled && s.dim, selected && s.sel].filter(Boolean).join(' ')}>
      {to ? <Link to={to} className={s.cover}>{content}</Link> : <div className={s.cover}>{content}</div>}
      <footer className={s.f}>
        <span>{act.who ? `${act.who} ` : ''}{act.verb} · <span className="tnum">{dateTime(act.at)}</span></span>
        {actions || <span className={s.next}>
          {x.status === 'DRAFT' && '待确认'}
          {x.status === 'CONFIRMED' && '待锁账'}
          {x.status === 'LOCKED' && !x.pdfFileUrl && '待生成 PDF'}
          {cancelled && '已作废'}
        </span>}
        <PdfLink url={x.pdfFileUrl} />
      </footer>
    </article>
  );
}

export const statementCols: Col<StatementListItem>[] = [
  { key: 'no', title: '对账单号', w: 170, fix: true, render: (x) => <b className={`tnum ${s.no}`}>{x.statementNo}</b> },
  {
    key: 'p', title: '往来单位', w: 150,
    render: (x) => <div className={s.cell2}><span>{x.partner?.name || '—'}</span><small>{LABELS[scopeOf(x)].partner}</small></div>,
  },
  { key: 'm', title: '结算月份', w: 88, render: (x) => <span className="tnum">{x.settlementMonth}</span> },
  { key: 'st', title: '状态', w: 80, render: (x) => <Tag tone={statementStatus[x.status].tone} sm>{statementStatus[x.status].label}</Tag> },
  { key: 'b', title: '业务金额', w: 116, align: 'r', render: (x) => (x.billAmount === undefined || x.status === 'CANCELLED' ? <span className="em">—</span> : money(x.billAmount)) },
  { key: 'c', title: '已收 / 已付', w: 116, align: 'r', render: (x) => (x.cashAmount === undefined || x.status === 'CANCELLED' ? <span className="em">—</span> : money(x.cashAmount)) },
  { key: 'o', title: '未收 / 未付', w: 116, align: 'r', render: (x) => (x.status === 'CANCELLED' ? <span className="em">—</span> : <Owe v={x.cashBalance} />) },
  {
    key: 'i', title: '待开 / 待收票', w: 116, align: 'r',
    render: (x) => { const n = num(x.invoiceBalance); return x.invoiceBalance === undefined || x.status === 'CANCELLED' ? <span className="em">—</span> : <span className={n > EPS ? s.warn : 'em'}>{signedMoney(n)}</span>; },
  },
  {
    key: 'a', title: '最近动作', w: 130,
    render: (x) => { const a = lastAction(x); return <div className={s.cell2}><span>{a.who ? `${a.who} ` : ''}{a.verb}</span><small className="tnum">{dateTime(a.at)}</small></div>; },
  },
  { key: 'pdf', title: 'PDF', w: 60, render: (x) => (x.pdfFileUrl ? <PdfLink url={x.pdfFileUrl} compact /> : <span className="em">—</span>) },
];
